import getRetailerIdvStatus from "@faire/web-api/api/retailer-idv-status/retailerToken/get";
import getDraftIDVRequest from "@faire/web-api/api/retailer/draft-identity-verification-request/get";
import { IIdentityVerificationRequest } from "@faire/web-api/indigofair/data/IIdentityVerificationRequest";
import { IGetRetailerIdvStatusResponse } from "@faire/web-api/indigofair/retailer/IGetRetailerIdvStatusResponse";
import { makeObservable } from "@faire/web/common/makeObservable";
import { singletonGetter } from "@faire/web/common/singletons/getter";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { createStoreHook } from "@faire/web/ui/hooks/useStore";
import { computed, observable, _allowStateChangesInsideComputed } from "mobx";
import { lazyObservable } from "mobx-utils";

import { RetailerStore } from "./RetailerStore";

type Status = "unknown" | "yes" | "no";

/**
 * @deprecated prefer to use `useIdvStatusStore` or `withStores` instead
 */
export class IdvStatusStore {
  constructor() {
    makeObservable(this);
  }

  /**
   * @trackfunction
   */
  static get = singletonGetter(IdvStatusStore);

  refreshRetailerIdvStatus = () => {
    this.retailerIdvStatus.refresh();
  };

  refreshDraftIDVRequest = () => {
    this.draftIdentityVerificationRequest.refresh();
  };

  @observable
  private retailerIdvStatus = lazyObservable<
    IGetRetailerIdvStatusResponse.IdvStatus | undefined
  >((sink) => {
    fetchRetailerIdvStatus().then((retailerIdvStatus) =>
      sink(retailerIdvStatus)
    );
  });

  @observable
  private draftIdentityVerificationRequest = lazyObservable<
    IIdentityVerificationRequest | undefined
  >((sink) => {
    getDraftIDVRequest()
      .then((draftIDVRequest) => sink(draftIDVRequest))
      .catch(() => sink(undefined));
  });

  @computed
  private get currentRetailerIdvStatus() {
    return _allowStateChangesInsideComputed(() =>
      this.retailerIdvStatus.current()
    );
  }

  @computed
  private get currentDraftIdentityVerificationRequest() {
    return _allowStateChangesInsideComputed(() =>
      this.draftIdentityVerificationRequest.current()
    );
  }

  @computed
  get hasPendingIdv(): Status {
    return this.isRetailerIdvStatus(
      IGetRetailerIdvStatusResponse.IdvStatus.HAS_PENDING_IDV_REQUEST
    );
  }

  @computed
  get hasNeverReceivedIdv(): Status {
    return this.isRetailerIdvStatus(
      IGetRetailerIdvStatusResponse.IdvStatus.NEVER_RECEIVED_IDV
    );
  }

  @computed
  get hasIdvRequestResponded(): Status {
    return this.isRetailerIdvStatus(
      IGetRetailerIdvStatusResponse.IdvStatus.IDV_REQUEST_RESPONDED
    );
  }

  @computed
  get hasDraftIdv(): Status {
    const request = this.currentDraftIdentityVerificationRequest;
    if (request === undefined) {
      return "unknown";
    } else if (
      this.hasPendingIdv === "yes" &&
      isNotUndefined(request.retailer_token)
    ) {
      return "yes";
    } else {
      return "no";
    }
  }

  private isRetailerIdvStatus = (
    retailerIdvStatus: IGetRetailerIdvStatusResponse.IdvStatus
  ): Status => {
    const currentRetailerIdvStatus = this.currentRetailerIdvStatus;
    if (currentRetailerIdvStatus === undefined) {
      return "unknown";
    }
    return currentRetailerIdvStatus === retailerIdvStatus ? "yes" : "no";
  };
}

export const useIdvStatusStore = createStoreHook(IdvStatusStore);

export const fetchRetailerIdvStatus =
  async (): Promise<IGetRetailerIdvStatusResponse.IdvStatus> => {
    if (RetailerStore.get().retailer?.identity_confirmed) {
      return Promise.resolve(
        IGetRetailerIdvStatusResponse.IdvStatus.IDV_REQUEST_RESPONDED
      );
    }
    try {
      const response = await getRetailerIdvStatus(
        RetailerStore.get().retailerToken!
      );
      return response.idv_status
        ? (response.idv_status as IGetRetailerIdvStatusResponse.IdvStatus)
        : IGetRetailerIdvStatusResponse.IdvStatus.IDV_STATUS_UNKNOWN;
    } catch {
      return IGetRetailerIdvStatusResponse.IdvStatus.IDV_STATUS_UNKNOWN;
    }
  };
